import React from 'react';
import { ProviderType } from '~/util/providerUtils';
import { AlertContext, AlertContextType, externalReferralAttachmentError } from './AlertContext';

export interface ReferralContextType {
    selectedProvider: ProviderType | null;
    setSelectedProvider: (provider: ProviderType) => void;
    clearSelectedProvider: () => void;
    externalReferralAttachment: ExternalAttachmentType | null;
    clearExternalReferralAttachment: () => void;
}

const ReferralContext = React.createContext<ReferralContextType | null>(null);

function ReferralProvider(props: { children: JSX.Element; show: boolean }) {
    const alertContext = React.useContext(AlertContext) as AlertContextType;
    const [selectedProvider, setSelectedProvider] = React.useState<ProviderType | null>(null);
    const [externalReferralAttachment, setExternalReferralAttachment] =
        React.useState<ExternalAttachmentType | null>(null);

    function clearSelectedProvider() {
        setSelectedProvider(null);
    }

    const clearExternalReferralAttachment = React.useCallback(() => {
        setExternalReferralAttachment(null);
    }, []);

    // add an event listener for adding external referral attachments
    React.useEffect(() => {
        function pageListener(request: MessageEvent<ExternalAttachmentEventType>) {
            if (request.source !== window || request.origin !== window.location.origin) return;
            if (
                request.data.method === 'documentPrint' &&
                request.data.data.destination === 'zephyr-referral'
            ) {
                // if no selected provider, then display an error
                if (!selectedProvider || !props.show) {
                    alertContext.set(
                        externalReferralAttachmentError,
                        'Document Upload Error',
                        0,
                        false
                    );
                } else {
                    setExternalReferralAttachment(request.data.data);
                }
            }
        }
        window.addEventListener('message', pageListener);
        return () => {
            window.removeEventListener('message', pageListener);
        };
    }, [selectedProvider, props.show, setExternalReferralAttachment, alertContext]);

    const methods: ReferralContextType = {
        selectedProvider,
        setSelectedProvider,
        clearSelectedProvider,
        externalReferralAttachment,
        clearExternalReferralAttachment,
    };
    return <ReferralContext.Provider value={methods} {...props} />;
}

export { ReferralContext, ReferralProvider };
